import {
  Alert,
  Box,
  Button,
  Card,
  Group,
  NativeSelect,
  Radio,
  Stack,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOrgs } from '../api/orgs';
import { useCreatePlatformUser, useRolesForOrg } from '../api/platform-users';
import { ApiError } from '../api/client';

type UserType = 'clinician' | 'patient' | 'user' | 'documentation';

export function PlatformUserCreatePage() {
  const navigate = useNavigate();
  const create = useCreatePlatformUser();
  const { data: orgs } = useOrgs();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userType, setUserType] = useState<UserType | null>(null);
  const [orgId, setOrgId] = useState<string>('');
  const [roleId, setRoleId] = useState<string>('');

  // Practitioner-only:
  const [profId, setProfId] = useState('');
  const [profIdType, setProfIdType] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [credentials, setCredentials] = useState('');

  const [error, setError] = useState<string | null>(null);

  const roles = useRolesForOrg(orgId || undefined);

  const isDocs = userType === 'documentation';
  // Patients don't pick a role: the per-org Patient role is auto-provisioned
  // server-side on first patient creation (see users.service.ts).
  const needsRole = !isDocs && userType !== 'patient';

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!userType || (!isDocs && !orgId) || (needsRole && !roleId)) {
      setError('Please complete all required fields.');
      return;
    }

    if (userType === 'clinician') {
      if (!profId || !profIdType || !speciality) {
        setError('Practitioner credentials (professional ID, ID type, speciality) are required.');
        return;
      }
    }

    create.mutate(
      {
        email,
        display_name: displayName,
        user_type: userType,
        organisation_id: isDocs ? undefined : orgId,
        role_id: needsRole ? roleId : undefined,
        professional_id: userType === 'clinician' ? profId : undefined,
        professional_id_type: userType === 'clinician' ? profIdType : undefined,
        speciality: userType === 'clinician' ? speciality : undefined,
        credentials: userType === 'clinician' ? credentials || undefined : undefined,
      },
      {
        onSuccess: (created) => {
          notifications.show({
            color: 'green',
            // Deliberately vague: user-management may be running with
            // COGNITO_PROVIDER=mock locally, in which case no email
            // actually goes out. "Invitation queued" is true either way.
            message: 'User created — login invitation queued.',
          });
          navigate(`/users/${created.id}/edit`);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setError('An account with this email already exists.');
          } else if (err instanceof ApiError && err.status === 400) {
            setError('Validation failed. Check the form and try again.');
          } else {
            setError('Could not create user. Try again.');
          }
        },
      },
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Create user</Title>
      <Card withBorder padding="lg" maw={680}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {error}
              </Alert>
            )}
            <TextInput
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={create.isPending}
            />
            <TextInput
              label="Display name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={create.isPending}
            />
            <Radio.Group
              label="User type"
              required
              value={userType ?? ''}
              onChange={(v) => {
                // Documentation users are org-less; clear any prior org/role
                // selection so switching types can't carry stale state.
                if (v === 'documentation') {
                  setOrgId('');
                  setRoleId('');
                }
                setUserType(v as UserType);
              }}
            >
              <Group mt="xs">
                <Radio value="clinician" label="Practitioner" />
                <Radio value="patient" label="Patient" />
                <Radio value="user" label="User" />
                <Radio value="documentation" label="Documentation" />
              </Group>
            </Radio.Group>
            {!isDocs && (
              <>
                <NativeSelect
                  label="Organisation"
                  required
                  value={orgId}
                  onChange={(e) => {
                    setOrgId(e.currentTarget.value);
                    setRoleId('');
                  }}
                  disabled={create.isPending}
                  data={[
                    { value: '', label: 'Select organisation…' },
                    ...(orgs ?? []).map((o) => ({ value: o.id, label: o.name })),
                  ]}
                />
                {needsRole && (
                  <NativeSelect
                    label="Role"
                    required
                    value={roleId}
                    onChange={(e) => setRoleId(e.currentTarget.value)}
                    disabled={!orgId || create.isPending}
                    data={[
                      { value: '', label: orgId ? 'Select role…' : 'Pick organisation first' },
                      ...(roles.data ?? []).map((r) => ({ value: r.id, label: r.name })),
                    ]}
                  />
                )}
              </>
            )}

            {userType === 'clinician' && (
              <Card withBorder padding="md" bg="gray.0">
                <Stack gap="md">
                  <Title order={5}>Practitioner credentials</Title>
                  <TextInput
                    label="Professional ID"
                    required
                    value={profId}
                    onChange={(e) => setProfId(e.currentTarget.value)}
                    disabled={create.isPending}
                  />
                  <NativeSelect
                    label="Professional ID type"
                    required
                    value={profIdType}
                    onChange={(e) => setProfIdType(e.currentTarget.value)}
                    disabled={create.isPending}
                    data={[
                      { value: '', label: 'Select type…' },
                      { value: 'GMC', label: 'GMC' },
                      { value: 'NMC', label: 'NMC' },
                      { value: 'NPI', label: 'NPI (US)' },
                      { value: 'OTHER', label: 'Other' },
                    ]}
                  />
                  <TextInput
                    label="Speciality"
                    required
                    value={speciality}
                    onChange={(e) => setSpeciality(e.currentTarget.value)}
                    disabled={create.isPending}
                  />
                  <TextInput
                    label="Credentials (optional)"
                    value={credentials}
                    onChange={(e) => setCredentials(e.currentTarget.value)}
                    disabled={create.isPending}
                  />
                </Stack>
              </Card>
            )}

            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" component={Link} to="/users">
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Create
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>
    </Stack>
  );
}
